import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useContent } from '../../lib/ContentContext';
import { assetUrl } from '../../lib/api';
import { ContactModal } from './ContactModal';

const NAV = [
  { label: 'Home', to: '/' },
  { label: 'About Us', to: '/about-us' },
  {
    label: 'Industries',
    children: [
      { label: 'Textile Chemicals', to: '/textile-chemicals' },
      { label: 'Leather Chemicals', to: '/leather-chemicals' },
      { label: 'Paper Chemicals', to: '/paper-chemicals' },
      { label: 'Paints & Coatings Chemicals', to: '/paints-coatings-chemicals' },
    ],
  },
  { label: 'R&D & Sustainability', to: '/rd-sustainability' },
  { label: 'News & Insights', to: '/news-insights' },
  { label: 'Contact', to: '/contact-us' },
];

export const Header = () => {
  const { content } = useContent();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const location = useLocation();
  const site = content?.site || {};

  const isIndustryActive = ['/textile-chemicals', '/leather-chemicals', '/paper-chemicals', '/paints-coatings-chemicals'].some((p) =>
    location.pathname.startsWith(p)
  );

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-white border-b border-[#E6E8EE] shadow-[0_2px_10px_rgba(17,24,39,0.05)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-24 flex items-center justify-between gap-4">
        <Link to="/" data-testid="site-header-logo" className="flex items-center shrink-0">
          {site.logo ? (
            <img src={assetUrl(site.logo)} alt={site.logo_alt || 'Intexso Biochem'} className="h-[85px] w-auto object-contain" />
          ) : (
            <span className="text-2xl font-extrabold text-[#26247B]">Intexso</span>
          )}
        </Link>

        {/* Desktop nav */}
        <nav data-testid="site-header-nav" className="hidden lg:flex items-center gap-7">
          {NAV.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setIndustriesOpen(true)}
                onMouseLeave={() => setIndustriesOpen(false)}
              >
                <button
                  data-testid="site-header-industries-dropdown"
                  className={`flex items-center gap-1 text-[16px] font-semibold tracking-wide transition-colors duration-200 ${
                    isIndustryActive ? 'text-[#2E8B57]' : 'text-[#26247B] hover:text-[#2E8B57]'
                  }`}
                >
                  {item.label}
                  <ChevronDown className="h-4 w-4" />
                </button>
                {industriesOpen && (
                  <div className="absolute left-0 top-full pt-3 w-72">
                    <div className="bg-white rounded-xl border border-[#E6E8EE] shadow-[0_14px_30px_rgba(17,24,39,0.14)] py-2">
                      {item.children.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          data-testid={`nav-link-${child.to.replace(/\//g, '')}`}
                          className={({ isActive }) =>
                            `block px-5 py-2.5 text-[16px] font-semibold transition-colors duration-200 ${
                              isActive ? 'text-[#2E8B57] bg-[#F6F7F9]' : 'text-[#26247B] hover:bg-[#F6F7F9] hover:text-[#2E8B57]'
                            }`
                          }
                        >
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.to === '/' ? 'home' : item.to.replace(/\//g, '')}`}
                className={({ isActive }) =>
                  `text-[16px] font-semibold tracking-wide transition-colors duration-200 ${
                    isActive ? 'text-[#2E8B57]' : 'text-[#26247B] hover:text-[#2E8B57]'
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setContactOpen(true)}
            data-testid="site-header-get-in-touch-button"
            className="hidden sm:inline-flex rounded-md bg-[#26247B] text-white text-sm font-bold tracking-wide px-6 py-3 transition-colors duration-200 hover:bg-[#1f1d66]"
          >
            GET IN TOUCH
          </button>
          <button
            data-testid="site-header-mobile-menu-button"
            className="lg:hidden p-2 text-[#26247B]"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-[#E6E8EE] max-h-[70vh] overflow-y-auto">
          <nav className="px-4 py-3 space-y-1">
            {NAV.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <div className="px-3 py-2 text-[16px] font-bold text-[#26247B]">{item.label}</div>
                  {item.children.map((child) => (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      onClick={() => setMobileOpen(false)}
                      className="block px-6 py-2 text-[16px] font-semibold text-[#26247B] hover:text-[#2E8B57]"
                    >
                      {child.label}
                    </NavLink>
                  ))}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2 text-[16px] font-semibold text-[#26247B] hover:text-[#2E8B57]"
                >
                  {item.label}
                </NavLink>
              )
            )}
            <button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                setContactOpen(true);
              }}
              className="block w-[calc(100%-1.5rem)] mt-2 mx-3 rounded-md bg-[#26247B] text-white text-center text-sm font-bold px-6 py-3"
            >
              GET IN TOUCH
            </button>
          </nav>
        </div>
      )}
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </header>
  );
};
